<template>
	<article :class="bemm()" :style="statStyle">
		<div :class="bemm('content')">
			<p :class="bemm('value')">{{ displayValue }}</p>
		</div>
		<footer :class="bemm('footer')" v-if="icon || title">
			<div :class="bemm('icon')">
				<Icon :name="icon" :size="Size.LARGE" />
			</div>
			<span :class="bemm('title')">{{ title }}</span>
		</footer>
	</article>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useBemm } from 'bemm';
import { Icon } from '../Icon';
import { Color, Size } from '@arritech/shared/types';
import { formatCompactNumber } from '@arritech/shared/utils';
import { StatCardProps } from './StatCard.model';

defineOptions({
	name: 'StatCard',
});

const props = withDefaults(defineProps<StatCardProps>(), {
	color: Color.PRIMARY,
});

const bemm = useBemm('stat-card', {
	return: 'string',
	includeBaseClass: true,
});

const statStyle = computed(() => ({
	'--stat-card-color': `var(--color-${props.color})`,
}));

const displayValue = computed(() => {
	if (props.value === null || typeof props.value === 'undefined') return '-';
	if (typeof props.value === 'number') {
		return formatCompactNumber(props.value);
	}
	return props.value;
});
</script>

<style lang="scss">
.stat-card {
	display: flex;
	align-items: center;
	gap: var(--space-s);
	padding: var(--space-s);
	// border: 1px solid color-mix(in srgb, var(--stat-card-color), transparent 74%);
	border-radius: var(--border-radius);
	background: color-mix(in srgb, var(--stat-card-color), transparent 94%);
	justify-content: center;
	flex-direction: column;
	width: 100%;
	opacity: 0;
	transform: scale(0.5);
	animation: popInCard 0.3s calc(var(--stat-card-index, 0) * 0.1s) ease-out
		forwards;

	@keyframes popInCard {
		to {
			opacity: 1;
			transform: scale(1);
		}
	}

	&__footer {
		display: flex;
		width: 100%;
		gap: var(--space-s);
	}

	&__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: var(--stat-card-color);
		flex: 0 0 auto;
		font-size: 0.75em;
	}

	&__content {
		display: flex;
		flex-direction: column;
		min-width: 0;
		height: 100%;
		align-items: center;
		justify-content: center;
		padding: var(--space);
	}

	&__title {
		margin: 0;
		font-size: var(--font-size);
		color: color-mix(in srgb, var(--color-foreground), transparent 20%);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		width: 100%;
	}

	&__value {
		margin: 0;
		font-size: 4vh;
		font-weight: var(--font-weight-extra-bold);
		color: var(--color-foreground);
		line-height: 1.1;
		color: var(--stat-card-color);
	}
}
</style>
